import { useCallback, useMemo } from 'react';
import { useDecision } from '@optimizely/react-sdk';

import { RUNTIME_ENV } from '@frontend/envConfig';
import { Tool, ToolService } from '@frontend/types';

import { useOperations } from './useOperations';

export const OCP_URL_PREFIX = 'https://function.zaius.app';

export type ToolCategory = 'ocp' | 'thirdparty' | 'system';

export function getToolCategory(tool: Tool): ToolCategory {
  if (tool.is_system_function) return 'system';
  if (tool.service.discovery_url?.startsWith(OCP_URL_PREFIX)) return 'ocp';
  return 'thirdparty';
}

export function isOcpUrl(url: string): boolean {
  return url.startsWith(OCP_URL_PREFIX);
}

export function useToolPermissions() {
  const { canUser, canInstance } = useOperations();
  const [systemToolsDecision] = useDecision('manage_system_tools');

  const permissions = useMemo(() => {
    // Manage permissions require both user permission and instance capability
    const canManageOcpTools = canUser(['manage_ocp_tools']) && canInstance(['ocp_tools_enabled']);
    const canManageThirdpartyTools =
      canUser(['manage_thirdparty_tools']) && canInstance(['external_integrations_enabled']);
    const canManageSystemTools = systemToolsDecision.enabled as boolean;

    // Adding registries requires both user permission and instance capability
    const canAddOcpRegistry = canManageOcpTools;
    const canAddThirdpartyRegistry = canManageThirdpartyTools;
    const canAddSystemRegistry = systemToolsDecision.enabled as boolean;
    const canAddAnyRegistry = canAddOcpRegistry || canAddThirdpartyRegistry || canAddSystemRegistry;

    // Delete permissions - user permission only, no instance capability check
    const canDeleteOcpRegistry = canUser(['manage_ocp_tools']);
    const canDeleteThirdpartyRegistry = canUser(['manage_thirdparty_tools']);
    // System registry deletion: allow in localdev OR if feature flag enabled
    const canDeleteSystemRegistry =
      RUNTIME_ENV === 'localdev' || (systemToolsDecision.enabled as boolean);

    return {
      canManageOcpTools,
      canManageThirdpartyTools,
      canManageSystemTools,
      canAddOcpRegistry,
      canAddThirdpartyRegistry,
      canAddSystemRegistry,
      canAddAnyRegistry,
      canDeleteOcpRegistry,
      canDeleteThirdpartyRegistry,
      canDeleteSystemRegistry,
    };
  }, [canUser, canInstance, systemToolsDecision.enabled]);

  const canManageRegistry = useCallback(
    (discoveryUrl: string): boolean => {
      const isOcp = isOcpUrl(discoveryUrl);
      if (isOcp) return permissions.canManageOcpTools;
      return permissions.canManageThirdpartyTools;
    },
    [permissions.canManageOcpTools, permissions.canManageThirdpartyTools]
  );

  const canManageTool = useCallback(
    (tool: Tool): boolean => {
      const category = getToolCategory(tool);
      if (category === 'system') return permissions.canManageSystemTools;
      if (category === 'ocp') return permissions.canManageOcpTools;
      if (category === 'thirdparty') return permissions.canManageThirdpartyTools;
      return false;
    },
    [
      permissions.canManageOcpTools,
      permissions.canManageThirdpartyTools,
      permissions.canManageSystemTools,
    ]
  );

  const getToolManageBlockReason = useCallback(
    (tool: Tool): string | null => {
      if (!permissions.canManageOcpTools && !permissions.canManageThirdpartyTools) {
        return 'You do not have permission to manage tools';
      }

      const category = getToolCategory(tool);
      if (category === 'ocp' && !permissions.canManageOcpTools) {
        return 'You do not have permission to manage OCP tools';
      }
      if (category === 'thirdparty' && !permissions.canManageThirdpartyTools) {
        return 'You do not have permission to manage third-party tools';
      }

      return null;
    },
    [permissions.canManageOcpTools, permissions.canManageThirdpartyTools]
  );

  const validateRegistryUrl = useCallback(
    (url: string, isSystemRegistry = false): { valid: boolean; error?: string } => {
      // System registries can be any URL
      if (isSystemRegistry) {
        if (!permissions.canAddSystemRegistry) {
          return {
            valid: false,
            error: 'You do not have permission to add system tool registries',
          };
        }
        return { valid: true };
      }

      // Regular registries are validated based on URL
      const isOcp = isOcpUrl(url);

      if (isOcp && !permissions.canAddOcpRegistry) {
        return {
          valid: false,
          error: 'You do not have permission to add OCP tool registries',
        };
      }
      if (!isOcp && !permissions.canAddThirdpartyRegistry) {
        return {
          valid: false,
          error: 'You do not have permission to add third-party tool registries',
        };
      }
      return { valid: true };
    },
    [
      permissions.canAddOcpRegistry,
      permissions.canAddThirdpartyRegistry,
      permissions.canAddSystemRegistry,
    ]
  );

  const getRegistryUrlHint = useCallback((): string | undefined => {
    const canAddBoth = permissions.canAddOcpRegistry && permissions.canAddThirdpartyRegistry;
    if (canAddBoth) return undefined;

    if (permissions.canAddOcpRegistry && !permissions.canAddThirdpartyRegistry) {
      return 'You can only add OCP tool registries (URLs starting with https://function.zaius.app)';
    }
    if (!permissions.canAddOcpRegistry && permissions.canAddThirdpartyRegistry) {
      return 'You can only add third-party tool registries';
    }
    return undefined;
  }, [permissions.canAddOcpRegistry, permissions.canAddThirdpartyRegistry]);

  const canDeleteRegistry = useCallback(
    (registry: Pick<ToolService, 'organization_id' | 'discovery_url'>): boolean => {
      const isSystemRegistry = !registry.organization_id;

      if (isSystemRegistry) {
        return permissions.canDeleteSystemRegistry;
      }

      // Check permission based on registry URL type
      const isOcp = isOcpUrl(registry.discovery_url);
      return isOcp ? permissions.canDeleteOcpRegistry : permissions.canDeleteThirdpartyRegistry;
    },
    [
      permissions.canDeleteOcpRegistry,
      permissions.canDeleteThirdpartyRegistry,
      permissions.canDeleteSystemRegistry,
    ]
  );

  const canSyncRegistry = useCallback(
    (registry: Pick<ToolService, 'organization_id' | 'discovery_url'>): boolean => {
      const isSystemRegistry = !registry.organization_id;

      if (isSystemRegistry) {
        // System tools sync is handled by backend (requires Optimizely email)
        return true;
      }

      // Check permission based on registry URL type (OCP vs third-party)
      return canManageRegistry(registry.discovery_url);
    },
    [canManageRegistry]
  );

  return {
    ...permissions,
    canManageTool,
    canManageRegistry,
    getToolManageBlockReason,
    getToolCategory,
    validateRegistryUrl,
    getRegistryUrlHint,
    canDeleteRegistry,
    canSyncRegistry,
  };
}
